import {SoftwareEvaluationStatus, SoftwareEvaluationResult, SoftwareEvaluation, SoftwareIndicatorDimension, SoftwareEvaluationArtefact, SoftwareEvaluationReportingInformation, PoFTopicNode} from '~/components/software/indicator/apiSoftwareIndicator'
import {createJsonHeaders, getBaseUrl} from '~/utils/fetchHelpers'
import logger from '~/utils/logger'

export type SoftwareEvaluationStatusForReporting = Pick<SoftwareEvaluationStatus, 'submit_ready' | 'submitted'> & {
  last_updated: SoftwareEvaluationStatus['changed_at']
  evaluation_result: SoftwareEvaluationResult
}

export type SoftwareEvaluationDataForReporting = {
  id: SoftwareEvaluation['id']
  software: {
    id: string
    slug: string
    name: string
  }
  year: SoftwareIndicatorDimension['year']
  current_artefact: SoftwareEvaluationArtefact | null
  centre_reporting_information: SoftwareEvaluationReportingInformation
  participating_centres: string[] //organisation slugs
  current_evaluation_status: SoftwareEvaluationStatusForReporting
}

export async function getSoftwareQualityEvaluationDataForOrganisation({organisation, year, token}: {organisation: string, year: string, token: string}) {
  try {
    const url = getBaseUrl() + '/rpc/software_quality_evaluation_data_for_organisation'
    const resp = await fetch(url, {
      method: 'POST',
      headers: createJsonHeaders(token),
      body: JSON.stringify({
        organisation_id: organisation,
        evaluation_year: year
      })
    })
    if (resp.status === 200) {
      const data: SoftwareEvaluationDataForReporting[] = await resp.json()
      return data
    }
    logger(`getSoftwareQualityEvaluationDataForOrganisation not 200: ${resp.status} - ${resp.body}`, 'error')
    return []
  } catch (e: any) {
    logger(`getSoftwareQualityEvaluationDataForOrganisation: ${e?.message}`, 'error')
    return []
  }
}

export type SavedQualityReport = {
  id: number
  organisation: string
  creator: string
  timestamp: string
  is_new: boolean
}

export async function getSavedQualityReportsForOrganisation(
  {organisation, year, token }: {organisation: string, year: string, token: string}
) {
  try {
    const url = getBaseUrl() + '/rpc/saved_evaluation_reports_for_organisation'
    const body = JSON.stringify({
      organisation_id: organisation,
      evaluation_year: year,
    })

    const response = await fetch(url, {
      method: 'POST',
      headers: createJsonHeaders(token),
      body: body,
    })

    if (response.status === 200) {
      const reports: SavedQualityReport[] = await response.json()
      return reports
    } else {
      logger(`getSavedQualityReportsForOrganisation: ${response.status}`, 'error')
      return []
    }
  } catch (e: any) {
    logger(`getSavedQualityReportsForOrganisation: ${e?.message}`, 'error')
    return []
  }
}

export async function downloadQualityReportForOrganisation(
  {organisation, year, format, token }: {organisation: string, year: string, format: 'json' | 'csv', token: string}
) {
  try {
    const url = getBaseUrl() + '/rpc/download_evaluation_report'
    const headers = {
      'accept': format === 'csv' ? 'text/csv' : 'application/json',
      ...createJsonHeaders(token),
    }
    const body = JSON.stringify({
      'organisation_id': organisation,
      'evaluation_year': year,
    })

    const response = await fetch(url, {
      method: 'POST',
      headers: headers,
      body: body
    })

    if (response.status === 200) {
      const blob = await response.blob()
      return blob
    } else {
      logger(`downloadQualityReportForOrganisation: ${response.status}`, 'error')
      return null
    }
  } catch (e: any) {
    logger(`downloadQualityReportForOrganisation: ${e?.message}`, 'error')
    return null
  }
}

export type DownloadFormat = 'json' | 'csv'

export async function downloadSavedQualityReport(
  {id, format, token }: {id: number, format: DownloadFormat, token: string}
) {
  try {
    const url = getBaseUrl() + '/rpc/download_saved_evaluation_report'
    const headers = {
      'accept': format === 'csv' ? 'text/csv' : 'application/json',
      ...createJsonHeaders(token),
    }
    const body = JSON.stringify({'id': id})

    const response = await fetch(url, {
      method: 'POST',
      headers: headers,
      body: body
    })

    if (response.status === 200) {
      const blob = await response.blob()
      return blob
    } else {
      logger(`downloadQualityReportForOrganisation: ${response.status}`, 'error')
      return null
    }
  } catch (e: any) {
    logger(`downloadQualityReportForOrganisation: ${e?.message}`, 'error')
    return null
  }
}

export async function getPoFTopicTreesForOrganisation({organisation, token}: {organisation: string, token: string}) {
  try {
    const url = getBaseUrl() + '/rpc/get_organisation_pof_topic_trees'
    const resp = await fetch(url, {
      method: 'POST',
      headers: createJsonHeaders(token),
      body: JSON.stringify({
        organisation_id: organisation,
      })
    })
    if (resp.status === 200) {
      const data: PoFTopicNode[] = await resp.json()
      return data
    }
    logger(`getPoFTopicTreesForOrganisation not 200: ${resp.status} - ${resp.body}`, 'error')
    return []
  } catch (e: any) {
    logger(`getPoFTopicTreesForOrganisation: ${e?.message}`, 'error')
    return []
  }
}

type SoftwareEvaluationReportingInformationWithParentInformation = SoftwareEvaluationReportingInformation['pof_topic'] & {
  parent: SoftwareEvaluationReportingInformationWithParentInformation
}

export type AssociatedPoFTopic = SoftwareEvaluationReportingInformation['pof_topic'] & {
  ancestors?: SoftwareEvaluationReportingInformation['pof_topic'][]
}

export async function getPoFTopicsAssociatedWithOrganisation({organisation, token}: {organisation: string, token: string}) {
  try {
    const url = getBaseUrl() + `/pof_topic_for_organisation?organisation=eq.${organisation}&select=category(id,name,short_name,parent(id,name,short_name,parent(id,name,short_name)))`
    const resp = await fetch(url, {
      method: 'GET',
      headers: createJsonHeaders(token)
    })
    if (resp.status === 200 || resp.status === 204) {
      const data = await resp.json()
      const flatData: AssociatedPoFTopic[] = data.map((row: {category: SoftwareEvaluationReportingInformationWithParentInformation}) => ({
        id: row.category.id,
        name: row.category.name,
        short_name: row.category.short_name,
        ancestors: [row.category.parent, row.category.parent?.parent].filter((p): p is NonNullable<SoftwareEvaluationReportingInformationWithParentInformation> => !!p).map(({id, name, short_name}) => ({id, name, short_name}))
      }))
      return flatData
    }
    logger(`getPoFTopicsAssociatedWithOrganisation not 200/204: ${resp.status} - ${resp.body}`, 'error')
    return []
  } catch (e: any) {
    logger(`getPoFTopicsAssociatedWithOrganisation: ${e?.message}`, 'error')
    return []
  }
}

export async function associatePoFTopicWithOrganisation({organisation, topic, token}: {organisation: string, topic: string, token: string}) {
  try {
    const url = getBaseUrl() + '/pof_topic_for_organisation'
    const resp = await fetch(url, {
      method: 'POST',
      headers: createJsonHeaders(token),
      body: JSON.stringify({
        organisation: organisation,
        pof_topic: topic
      })
    })
    if (resp.status === 201) {
      return '200'
    }
    logger(`associatePoFTopicWithOrganisation not 201: ${resp.status} - ${resp.body}`, 'error')
    return []
  } catch (e: any) {
    logger(`associatePoFTopicWithOrganisation: ${e?.message}`, 'error')
    return []
  }
}

export async function deletePoFTopicAssociationWithOrganisation({organisation, topic, token}: {organisation: string, topic: string, token: string}) {
  try {
    const url = getBaseUrl() + `/pof_topic_for_organisation?organisation=eq.${organisation}&pof_topic=eq.${topic}`
    const resp = await fetch(url, {
      method: 'DELETE',
      headers: createJsonHeaders(token),
      body: JSON.stringify({
        organisation: organisation,
        pof_topic: topic
      })
    })
    if (resp.status === 201 || resp.status === 204) {
      return '200'
    }
    logger(`deletePoFTopicAssociationWithOrganisation not 201/204: ${resp.status} - ${resp.body}`, 'error')
    return []
  } catch (e: any) {
    logger(`deletePoFTopicAssociationWithOrganisation: ${e?.message}`, 'error')
    return []
  }
}
